import { useEffect } from "react";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const migrationDrivers = [
  {
    title: "Remote Work",
    description:
      "Remote work has transformed professionals' views on living locations, offering flexibility to choose where to live while maintaining careers across global markets.",
  },
  {
    title: "Lifestyle Choices",
    description:
      "Professionals are prioritizing work-life balance, family connections, and affordable living options, pushing interest toward emerging economies like India.",
  },
  {
    title: "Immigration Challenges",
    description:
      "Uncertainties in visa processes create relocation hesitation and increase interest in markets with fewer long-term barriers.",
  },
  {
    title: "Family Considerations",
    description:
      "Cultural connections, support systems, and a stronger sense of belonging are becoming central to relocation decisions.",
  },
];

const indiaGrowthFactors = [
  "GDP growth",
  "Over 100,000 startups driving innovation and employment",
  "Digital India initiatives accelerating economic transformation",
  "A $1.25 billion government commitment to AI innovation",
];

const jaipurIndustries = [
  {
    title: "Information Technology",
    description: "Jaipur is rapidly becoming an IT hub.",
  },
  {
    title: "Tourism",
    description: "The city attracts millions of tourists annually.",
  },
  {
    title: "Gems & Jewellery",
    description: "Jaipur is known for its world-class gemstones and export strength.",
  },
  {
    title: "Technology",
    description: "The city is becoming a tech hub in India.",
  },
  {
    title: "Education",
    description: "The city boasts top institutions and universities.",
  },
  {
    title: "Handicrafts",
    description: "Jaipur is renowned for exquisite handicrafts and jewelry.",
  },
];

const leadershipOpportunities = [
  "Senior management roles for experienced professionals and leaders",
  "A rising startup ecosystem fostering innovation and growth",
  "High-profile networking events creating valuable business connections",
  "Global work flexibility through remote team leadership",
];

const investmentThemes = [
  {
    title: "Real Estate",
    description:
      "Jaipur's real estate market offers affordable property prices and ongoing urban development, creating potential for strong returns.",
  },
  {
    title: "Technology Startups",
    description:
      "A growing tech ecosystem and government support create fertile ground for innovation and entrepreneurship.",
  },
  {
    title: "Export Industries",
    description:
      "Jaipur remains a global center for export activity, especially in gems, jewellery, and handicrafts.",
  },
];

const lifeBenefits = [
  {
    title: "Affordable Housing",
    description:
      "Housing options fit a wide range of budgets, reducing the financial strain common in larger metropolitan markets.",
  },
  {
    title: "Family-Friendly Environment",
    description:
      "Safe neighborhoods and strong community values create an inviting place for families to build a balanced life.",
  },
  {
    title: "International Schools",
    description:
      "Several international schools offer diverse curricula suited for expatriate and globally mobile families.",
  },
  {
    title: "Top Universities",
    description:
      "Reputable universities and colleges support higher education across disciplines and strengthen the local talent pipeline.",
  },
  {
    title: "Cultural Learning",
    description:
      "Families benefit from a rich cultural and historical setting that helps children stay connected to heritage while gaining a global outlook.",
  },
];

export default function IndiaEmergingHub() {
  useEffect(() => {
    document.title = "India Emerging Hub | Variable Soft";
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-[#f4efe8]"
      style={{ fontFamily: "'HK Grotesk', 'Roboto', sans-serif" }}
    >
      <Navigation />

      <section className="relative overflow-hidden bg-[#102742] px-4 pb-20 pt-32 text-white sm:px-6">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(800px_circle_at_12%_18%,rgba(255,255,255,0.12),transparent_42%),linear-gradient(135deg,#17385d_0%,#102742_48%,#0c2137_100%)]" />
          <div className="absolute -left-24 top-20 h-80 w-80 rounded-full bg-[#80c3d9]/10 blur-3xl" />
          <div className="absolute right-0 top-0 h-full w-[36%] bg-[#d9774a]/12" />
          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.7)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:48px_48px]" />
        </div>

        <div className="relative mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="grid items-end gap-10 lg:grid-cols-[1.15fr_0.85fr]"
          >
            <div className="max-w-4xl">
              <p className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-[#d7ecf6]">
                Presented by Variable Soft
              </p>
              <h1 className="mt-6 text-4xl font-black leading-[0.95] sm:text-5xl lg:text-7xl">
                India's
                <span className="block text-[#f6cbb4]">Emerging Hub</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-200">
                A presentation-led narrative on changing global migration,
                India's economic rise, and Jaipur's emergence as a practical
                destination for talent, investment, and leadership.
              </p>
            </div>

            <div className="rounded-[32px] border border-white/10 bg-white/10 p-8 backdrop-blur">
              <p className="text-sm uppercase tracking-[0.28em] text-[#d7ecf6]">
                Focus Areas
              </p>
              <div className="mt-6 space-y-4">
                {[
                  "Migration shifts",
                  "India growth and AI",
                  "Jaipur opportunity",
                ].map((item, index) => (
                  <div
                    key={item}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-4"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f08c5b] text-sm font-bold text-white">
                      0{index + 1}
                    </span>
                    <p className="text-base font-medium text-white">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Section 2 */}
      <section className="bg-[#f4efe8] px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-[40px] border border-[#d8cec1] bg-white shadow-[0_30px_80px_-52px_rgba(15,23,42,0.45)]">
            <div className="grid gap-8 p-6 sm:p-8">
              <div className="grid gap-6 rounded-[32px] bg-[#153557] px-6 py-8 text-white sm:px-8 lg:grid-cols-[1fr_0.9fr]">
                <div>
                  <p className="text-sm uppercase tracking-[0.3em] text-[#d7ecf6]">
                    A New Trend
                  </p>
                  <h2 className="mt-4 max-w-2xl text-3xl font-black leading-tight sm:text-4xl">
                    Changing Dynamics of Global Migration
                  </h2>
                </div>
                <div className="rounded-[24px] border border-white/10 bg-white/5 p-5">
                  <p className="leading-8 text-slate-300">
                    The deck frames migration not as a one-off relocation pattern,
                    but as a structural shift driven by remote work, affordability,
                    and lifestyle priorities.
                  </p>
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                {migrationDrivers.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 24, scale: 0.98 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="group rounded-[30px] border border-[#e5d8ca] bg-[linear-gradient(180deg,#fffaf3_0%,#ffffff_100%)] p-7 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.35)]"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#f08c5b] text-lg font-black text-white">
                        0{index + 1}
                      </div>
                      <div className="h-px flex-1 self-center bg-gradient-to-r from-[#f2c4ae] to-transparent" />
                    </div>
                    <h3 className="mt-6 text-2xl font-bold text-slate-950">
                      {item.title}
                    </h3>
                    <p className="mt-3 leading-8 text-slate-600">
                      {item.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 sm:px-6">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[34px] bg-[#fffaf3] p-8 shadow-[0_25px_70px_-45px_rgba(15,23,42,0.4)]">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#d9774a]">
              Key Factors Driving Migration Shifts
            </p>
            <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
              India's economic momentum is changing the decision framework.
            </h2>
            <div className="mt-8 space-y-4">
              {[
                "Rising living costs drive the search for alternatives.",
                "Rapid growth in India attracts global talent and investment.",
                "Potential for global professional leadership is expanding.",
                "Adoption is growing across multiple sectors.",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-4 rounded-2xl border border-[#ead8ca] bg-white p-5"
                >
                  <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#f08c5b]" />
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[34px] bg-[#153557] p-8 text-white">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#cde2f0]">
              India Growth Drivers
            </p>
            <div className="mt-6 space-y-4">
              {indiaGrowthFactors.map((item, index) => (
                <div
                  key={item}
                  className="rounded-2xl border border-white/10 bg-white/5 p-5"
                >
                  <p className="text-sm uppercase tracking-[0.18em] text-[#f6cbb4]">
                    0{index + 1}
                  </p>
                  <p className="mt-2 text-slate-200">{item}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-[#f08c5b]/20 bg-[#f08c5b]/10 p-5">
              <h3 className="text-xl font-bold text-white">
                AI Innovation Investment
              </h3>
              <p className="mt-3 leading-7 text-slate-300">
                The Indian government has committed $1.25 billion to foster AI
                innovation across infrastructure, research and development, and
                talent acquisition.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f4efe8] px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#d9774a]">
              Cultural Heritage
            </p>
            <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
              Jaipur combines heritage with economic expansion.
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              Jaipur, known as the Pink City, showcases stunning architecture,
              vibrant markets, and rich history while steadily strengthening its
              position as a practical destination for professionals and
              investors.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {jaipurIndustries.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                viewport={{ once: true }}
                className="rounded-[30px] border border-[#d9d0c3] bg-[#fffaf3] p-7 shadow-[0_20px_60px_-42px_rgba(2,6,23,0.35)]"
              >
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#d9774a]">
                  Growth Sector
                </p>
                <h3 className="text-2xl font-bold text-slate-950">
                  {item.title}
                </h3>
                <p className="mt-4 leading-7 text-slate-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#153557] px-4 py-20 text-white sm:px-6">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#cde2f0]">
              Leadership and Growth Opportunities in Jaipur
            </p>
            <h2 className="mt-4 text-3xl font-black sm:text-4xl">
              A city positioned for experienced professionals, founders, and
              remote leaders.
            </h2>
            <div className="mt-8 space-y-4">
              {leadershipOpportunities.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-5"
                >
                  <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#f6cbb4]" />
                  <p className="text-slate-300">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[32px] border border-white/10 bg-[#0f2943] p-8">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#cde2f0]">
              Remote Work Opportunities for Global Professionals
            </p>
            <div className="mt-6 space-y-6">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <h3 className="text-2xl font-bold">Global Work Flexibility</h3>
                <p className="mt-3 leading-8 text-slate-300">
                  Remote work enables professionals to lead international teams
                  from anywhere while managing projects effectively from Jaipur.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <h3 className="text-2xl font-bold">Enhanced Quality of Life</h3>
                <p className="mt-3 leading-8 text-slate-300">
                  Working remotely from Jaipur offers an affordable lifestyle,
                  cultural richness, and a supportive community for individuals
                  and families.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#d9774a]">
              Investing in Jaipur
            </p>
            <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
              Unlocking growth and opportunities
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {investmentThemes.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="rounded-[30px] border border-[#d9d0c3] bg-[#fffaf3] p-8"
              >
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#d9774a]">
                  Investment Theme
                </p>
                <h3 className="text-2xl font-bold text-slate-950">
                  {item.title}
                </h3>
                <p className="mt-4 leading-8 text-slate-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
 {/* Quality of Life */}
      <section className="bg-[#f4efe8] px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="overflow-hidden rounded-[40px] border border-[#d8cec1] bg-white shadow-[0_30px_80px_-52px_rgba(15,23,42,0.42)]">
            <div className="grid lg:grid-cols-[0.36fr_0.64fr]">
              <div className="relative overflow-hidden bg-[#fff7ef] p-8 sm:p-10">
                <div className="absolute -right-10 top-10 h-32 w-32 rounded-full bg-[#f08c5b]/12 blur-2xl" />
                <div className="absolute -left-10 bottom-8 h-28 w-28 rounded-full bg-[#153557]/8 blur-2xl" />

                <div className="relative space-y-6">
                  <div className="inline-flex rounded-full border border-[#efc9b6] bg-white px-4 py-2 text-sm font-semibold uppercase tracking-[0.24em] text-[#d9774a]">
                    Education Benefits
                  </div>

                  <div className="rounded-[28px] bg-[#153557] p-6 text-white">
                    <h2 className="text-3xl font-black leading-tight sm:text-[2.2rem]">
                      A nurturing environment for families
                    </h2>
                  </div>

                  <div className="rounded-[28px] border border-[#ead7c8] bg-white p-6">
                    <p className="text-lg leading-8 text-slate-600">
                      The presentation positions Jaipur not only as a business
                      destination, but as a city where family life, education, and
                      cultural continuity strengthen the relocation case.
                    </p>
                  </div>

                  <div className="grid gap-4">
                    <div className="rounded-[24px] bg-[#153557] p-5 text-white">
                      <p className="text-sm uppercase tracking-[0.22em] text-[#d7ecf6]">
                        Family Focus
                      </p>
                      <p className="mt-3 leading-7 text-slate-300">
                        Balanced living and community support remain central to the relocation appeal.
                      </p>
                    </div>
                    <div className="rounded-[24px] bg-[#f08c5b] p-5 text-white">
                      <p className="text-sm uppercase tracking-[0.22em] text-white/80">
                        Long-Term Value
                      </p>
                      <p className="mt-3 leading-7 text-white/85">
                        Education, affordability, and cultural continuity reinforce stability over time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#f4efe8] p-8 sm:p-10">
                <div className="max-w-3xl">
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#d9774a]">
                    Quality of Life
                  </p>
                  <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
                    Advantages of living in Jaipur
                  </h2>
                </div>

                <div className="mt-10 space-y-4">
                  {lifeBenefits.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: 24 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.45, delay: index * 0.06 }}
                      viewport={{ once: true }}
                      className="grid gap-4 rounded-[28px] border border-[#d9d0c3] bg-white p-6 sm:grid-cols-[56px_1fr]"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#153557] text-base font-black text-white">
                        0{index + 1}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-950">
                          {item.title}
                        </h3>
                        <p className="mt-3 leading-8 text-slate-600">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#102742] px-4 py-20 text-white sm:px-6">
        <div className="mx-auto max-w-5xl rounded-[36px] border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] p-10 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#cde2f0]">
            Reverse Migration
          </p>
          <h2 className="mt-4 text-3xl font-black sm:text-4xl">
            Professionals are returning to India for growth and connection to
            their roots.
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-300">
            India and Jaipur present a combined story of economic growth,
            leadership opportunities, cultural depth, and practical quality of
            life for globally minded professionals and investors.
          </p>

          <div className="mt-10 grid gap-4 text-left sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="text-sm uppercase tracking-[0.2em] text-[#f6cbb4]">
                Phone
              </p>
              <p className="mt-2 text-lg font-semibold">+91 73000 75187</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="text-sm uppercase tracking-[0.2em] text-[#f6cbb4]">
                Email
              </p>
              <p className="mt-2 text-lg font-semibold">
                info@variablesoft.com
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="text-sm uppercase tracking-[0.2em] text-[#f6cbb4]">
                Website
              </p>
              <p className="mt-2 text-lg font-semibold">www.variablesoft.com</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
